export type ShiftTypeIdx = 0 | 1 | 2;
export type ShiftTypeOption = 'dental' | 'admin';

export interface AdminTempingAccessInput {
    type?: number;
    country?: number;
    agreedAdmin?: string | null;
    sessionAgreed: boolean;
    membershipResolved: boolean;
    adminTempingGoodStanding: boolean;
    freshUserResolved: boolean;
}

export interface AvailableJobsFilterState {
    selectedSort?: number;
    distanceIdx?: number;
    rateIdx?: number;
    favourite?: boolean;
    shiftTypeIdx?: number;
}

export const resolveFreshAvailableJobsHygienist = async <T>(
    getUser: () => Promise<{hygienist?: T | null}>,
): Promise<T> => {
    const user = await getUser();
    if (!user?.hygienist) {
        throw new Error('Missing fresh hygienist eligibility data');
    }
    return user.hygienist;
};

export const normalizeShiftTypeIdx = (value?: number | null): ShiftTypeIdx => {
    return value === 1 || value === 2 ? value : 0;
};

export const getPreviousFiltersStorageKey = (hygienistId?: number): string | null => {
    return hygienistId && Number.isInteger(hygienistId) && hygienistId > 0
        ? `availableJobsPreviousFilters:${hygienistId}`
        : null;
};

export const canIncludeAdminShifts = ({
    type,
    country,
    agreedAdmin,
    sessionAgreed,
    membershipResolved,
    adminTempingGoodStanding,
    freshUserResolved,
}: AdminTempingAccessInput): boolean => {
    if (!freshUserResolved || type !== 1 || country !== 0) return false;

    if (agreedAdmin) {
        const agreedYear = new Date(agreedAdmin).getFullYear();
        return Number.isFinite(agreedYear) && agreedYear > 1970;
    }

    return membershipResolved && adminTempingGoodStanding && sessionAgreed;
};

export const clampShiftTypeIdx = (
    value: number | null | undefined,
    filterVisible: boolean,
): ShiftTypeIdx => {
    return filterVisible ? normalizeShiftTypeIdx(value) : 0;
};

export const shouldResetAvailableJobsFilters = (
    preserveFilters?: boolean,
    restoreListState?: boolean,
): boolean => !preserveFilters && !restoreListState;

export const areAvailableJobsFiltersDefault = (filters: AvailableJobsFilterState): boolean => {
    return (filters.selectedSort ?? 0) === 0
        && (filters.distanceIdx ?? 0) === 0
        && (filters.rateIdx ?? 0) === 0
        && (filters.favourite ?? false) === false
        && normalizeShiftTypeIdx(filters.shiftTypeIdx) === 0;
};

export const getPreviousFiltersAfterClear = <T extends AvailableJobsFilterState>(
    current: T,
    previous: T | null,
): T | null => areAvailableJobsFiltersDefault(current) ? previous : current;

export const buildAvailableJobsListKey = (
    dateKey: string,
    filters: AvailableJobsFilterState,
): string => {
    return [
        'available-jobs',
        dateKey || 'all',
        `sort-${filters.selectedSort ?? 0}`,
        `dist-${filters.distanceIdx ?? 0}`,
        `rate-${filters.rateIdx ?? 0}`,
        `fav-${filters.favourite ? 1 : 0}`,
        `type-${normalizeShiftTypeIdx(filters.shiftTypeIdx)}`,
    ].join('|');
};

export const filterJobsByShiftType = <T extends { type: number }>(
    jobs: T[],
    value?: number | null,
): T[] => {
    const normalizedValue = normalizeShiftTypeIdx(value);
    if (normalizedValue === 1) return jobs.filter(job => job.type === 2);
    if (normalizedValue === 2) return jobs.filter(job => job.type === 1);
    return [...jobs];
};

export const getNextShiftTypeIdx = (
    value: number | null | undefined,
    toggledOption: ShiftTypeOption,
): ShiftTypeIdx => {
    const normalizedValue = normalizeShiftTypeIdx(value);

    if (normalizedValue === 0) {
        return toggledOption === 'dental' ? 1 : 2;
    }

    if (normalizedValue === 1) {
        return toggledOption === 'admin' ? 1 : 0;
    }

    return toggledOption === 'dental' ? 2 : 0;
};

export const isShiftTypeOptionLocked = (
    value: number | null | undefined,
    option: ShiftTypeOption,
): boolean => {
    const normalizedValue = normalizeShiftTypeIdx(value);
    return (normalizedValue === 1 && option === 'admin')
        || (normalizedValue === 2 && option === 'dental');
};

export const getShiftTypeAnalyticsValue = (value?: number | null): string => {
    const normalizedValue = normalizeShiftTypeIdx(value);
    if (normalizedValue === 1) return 'admin_reception';
    if (normalizedValue === 2) return 'dental_assisting';
    return 'all';
};

export const getShiftTypeEmptyMessage = (value?: number | null): string | undefined => {
    const normalizedValue = normalizeShiftTypeIdx(value);
    if (normalizedValue === 1) {
        return 'No Admin/Reception shifts match your current filters. Choose Dental Assisting or adjust your filters.';
    }
    if (normalizedValue === 2) {
        return 'No Dental Assisting shifts match your current filters. Choose Admin/Reception or adjust your filters.';
    }
    return undefined;
};
